import { useRef, useState } from "react";
import { Image as ImageIcon, Upload, X, Loader2 } from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import { supabase } from "../../lib/supabase";
import { useToast } from "../Toast";

const MAX_BYTES = 1024 * 1024;
const ALLOWED_TYPES = ["image/png", "image/jpeg", "image/webp", "image/svg+xml"];

function extFromType(type) {
  if (type === "image/png") return "png";
  if (type === "image/jpeg") return "jpg";
  if (type === "image/webp") return "webp";
  if (type === "image/svg+xml") return "svg";
  return "png";
}

function pathFromUrl(url) {
  if (!url) return null;
  const marker = "/object/public/lead-logos/";
  const idx = url.indexOf(marker);
  if (idx === -1) return null;
  return url.slice(idx + marker.length);
}

export default function LeadCaptureLogoField({ value, onChange }) {
  const { user } = useAuth();
  const { showError, showSuccess } = useToast();
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const handlePick = () => inputRef.current?.click();

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;

    if (!ALLOWED_TYPES.includes(file.type)) {
      showError("Sube un PNG, JPG, WEBP o SVG");
      return;
    }
    if (file.size > MAX_BYTES) {
      showError("La imagen no puede pesar más de 1MB");
      return;
    }

    setUploading(true);
    try {
      const ext = extFromType(file.type);
      const path = `${user.id}/logo-${Date.now()}.${ext}`;
      const { error: upErr } = await supabase.storage
        .from("lead-logos")
        .upload(path, file, { cacheControl: "3600", upsert: false });
      if (upErr) {
        console.error("[logo-upload] supabase error", upErr);
        throw upErr;
      }

      const { data: pub } = supabase.storage
        .from("lead-logos")
        .getPublicUrl(path);
      const newUrl = pub?.publicUrl;
      if (!newUrl) throw new Error("No se pudo obtener la URL pública");

      const oldPath = pathFromUrl(value);
      if (oldPath && oldPath !== path) {
        await supabase.storage.from("lead-logos").remove([oldPath]);
      }

      onChange(newUrl);
      showSuccess("Logo subido. No olvides Guardar cambios.");
    } catch (err) {
      console.error("[logo-upload] failed", err);
      showError(err?.message || "No se pudo subir el logo");
    } finally {
      setUploading(false);
    }
  };

  const handleRemove = async () => {
    const oldPath = pathFromUrl(value);
    if (oldPath) {
      await supabase.storage.from("lead-logos").remove([oldPath]);
    }
    onChange(null);
  };

  return (
    <div>
      <span className="block text-sm font-medium text-gray-700 mb-1.5">
        Logo del centro (opcional)
      </span>

      <input
        ref={inputRef}
        type="file"
        accept={ALLOWED_TYPES.join(",")}
        onChange={handleFile}
        className="hidden"
      />

      {value ? (
        <div className="flex items-center gap-3 p-3 bg-gray-50 border border-gray-200 rounded-lg">
          <div className="w-14 h-14 bg-white border border-gray-100 rounded-md flex items-center justify-center overflow-hidden flex-shrink-0">
            <img
              src={value}
              alt="Logo"
              className="max-w-full max-h-full object-contain"
            />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-xs text-gray-500 mb-1.5">
              Aparece arriba del formulario público.
            </p>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={handlePick}
                disabled={uploading}
                className="text-xs px-2.5 py-1 rounded-md bg-white border border-gray-200 text-gray-700 hover:bg-gray-100 transition-colors disabled:opacity-50"
              >
                {uploading ? "Subiendo..." : "Cambiar"}
              </button>
              <button
                type="button"
                onClick={handleRemove}
                disabled={uploading}
                className="text-xs px-2.5 py-1 rounded-md text-red-600 hover:bg-red-50 transition-colors flex items-center gap-1 disabled:opacity-50"
              >
                <X size={12} />
                Quitar
              </button>
            </div>
          </div>
        </div>
      ) : (
        <button
          type="button"
          onClick={handlePick}
          disabled={uploading}
          className="w-full flex items-center justify-center gap-2 px-3 py-4 border-2 border-dashed border-gray-200 rounded-lg text-sm text-gray-500 hover:border-brand hover:text-brand hover:bg-brand/5 transition-colors disabled:opacity-50"
        >
          {uploading ? (
            <>
              <Loader2 size={16} className="animate-spin" />
              Subiendo...
            </>
          ) : (
            <>
              <Upload size={16} />
              Subir logo
            </>
          )}
        </button>
      )}

      <p className="mt-1 text-xs text-gray-500 flex items-center gap-1">
        <ImageIcon size={11} />
        PNG, JPG, WEBP o SVG. Máx. 1MB.
      </p>
    </div>
  );
}
